import React, { FC, useState, ChangeEvent, useEffect } from 'react'
import { I18n, Storage } from 'aws-amplify'
import { TextField, makeStyles, Grid, Theme, Typography } from '@material-ui/core'
import AccountCircleIcon from '@material-ui/icons/AccountCircle'
import { Autocomplete, createFilterOptions } from '@material-ui/lab'
import { useDropzone } from 'react-dropzone'
import { v4 as uuid } from 'uuid'
import axios from 'axios'

import { PillButton } from 'components'
import { AuthFlowSteps, IUser, IOption } from 'types'
import { validateNonNumeric } from 'helpers'
import { graphQLMutation } from 'graphql/helpers'
import { createUser } from 'graphql/mutations'

interface IIntegrateData extends Omit<IUser, 'jobTitle'> {
  title?: string
}

interface RegistrationProps {
  userEmail: string
  setAuthState: (state: AuthFlowSteps) => void
  setUser: (user: IUser) => void
}
interface IRegErrors {
  firstName: string
  lastName: string
  company: string
  jobTitle: string
  avatar: string
  address1: string
  address2: string
  city: string
  state: string
  postalCode: string
  country: string
}
export const Registration: FC<RegistrationProps> = ({ userEmail, setAuthState, setUser }) => {
  const classes = useStyles()
  const initialUser: IUser = {
    id: uuid(),
    firstName: '',
    lastName: '',
    email: userEmail,
    avatar: '',
    company: '',
    jobTitle: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  }
  const initialErrors: IRegErrors = {
    firstName: '',
    lastName: '',
    avatar: '',
    company: '',
    jobTitle: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  }
  const [userInfo, setUserInfo] = useState<IUser>(initialUser)
  const [loading, setLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<IRegErrors>(initialErrors)

  useEffect(() => {
    if (userEmail) {
      setUserInfo({ ...userInfo, email: userEmail })
    }
    // eslint-disable-next-line
  }, [userEmail])

  useEffect(() => {
    return () => {
      setUserInfo(initialUser)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    accept: 'image/jpeg, image/png'
  })

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e
    setUserInfo({
      ...userInfo,
      [target.name]: target.value
    })
  }

  const createNewUser = async () => {
    const lowerCaseEmail: string = userInfo.email?.toLowerCase() || ''
    try {
      if (acceptedFiles && acceptedFiles[0]) {
        const file = acceptedFiles[0]
        const avatar = `${userInfo.id}.${file.type.split('/')[1]}`

        // eslint-disable-next-line
        await Promise.all([
          Storage.put(avatar, file, { level: 'public', contentType: file.type }),
          graphQLMutation(createUser, {
            ...userInfo,
            email: lowerCaseEmail,
            avatar,
            online: true,
            private: false
          })
        ])
      } else {
        await graphQLMutation(createUser, { ...userInfo, email: lowerCaseEmail, online: true, private: false })
      }
    } catch (e) {
      console.log(e)
      return
    }
  }

  const sendIntegrateData = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const utmCampaignParams = {
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        utm_term: urlParams.get('utm_term') || '',
        utm_content: urlParams.get('utm_content') || ''
      }

      // a shared lambda for the integrate data expects title instead of jobTitle.
      // mapping that back to the expected here before sendign off
      const userInfoWithTitle = userInfo
      userInfoWithTitle.title = userInfo.jobTitle
      delete userInfoWithTitle?.jobTitle

      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'application/json'
        },
        url: 'https://f1chjtarbg.execute-api.us-east-1.amazonaws.com/prod/integrate',
        data: {
          user: { ...userInfoWithTitle, ...utmCampaignParams, createdAt: new Date().getTime(), status: 'Registered' },
          event: 'international'
        }
      })
      // Remove URL params
      window.history.replaceState({}, document.title, '/')
      return response.data
    } catch (error) {
      console.log(error)
    }
  }

  const validateForm = (): boolean => {
    const errorObj: IRegErrors = {
      firstName: !userInfo.firstName ? I18n.get('requiredField') : '',
      lastName: !userInfo.lastName ? I18n.get('requiredField') : '',
      company: !userInfo.company ? I18n.get('requiredField') : '',
      jobTitle: !userInfo.jobTitle ? I18n.get('requiredField') : '',
      // optional
      avatar: '',
      address1: '',
      address2: '',
      city: /\S+/g.test(userInfo.city || '')
        ? !validateNonNumeric(userInfo.city as string)
          ? I18n.get('invalidCity')
          : ''
        : '',
      state: /\S+/g.test(userInfo.state || '')
        ? !validateNonNumeric(userInfo.state as string)
          ? I18n.get('invalidCity')
          : ''
        : '',
      postalCode: '',
      country: !userInfo.country ? I18n.get('requiredField') : ''
    }

    const hasErrors = Object.keys(errorObj).some(key => errorObj[key] !== '')

    if (hasErrors) {
      setErrors(errorObj)
      return true
    }

    return false
  }

  const submitUser = async () => {
    const hasErrors = validateForm()
    if (!hasErrors) {
      setLoading(true)
      try {
        await createNewUser()
        await sendIntegrateData()
        // TODO: Remove later
        setAuthState(AuthFlowSteps.Interests)
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <Grid container direction='column' justify='center'>
      <Grid item>
        <Typography variant='h2' className={classes.heading} paragraph>
          <span dangerouslySetInnerHTML={{ __html: I18n.get('joinUs') }}></span>
        </Typography>
      </Grid>

      <Grid item container spacing={2}>
        <Grid item xs={12}>
          <AccountCircleIcon fontSize='large' classes={{ root: classes.inlineAvatarIcon }} />
          <div {...getRootProps()} className={classes.inlineAvatarUpload}>
            <input {...getInputProps()} />
            <PillButton
              loading={loading}
              onClick={() => {}}
              backgroundColor='transparent'
              className={classes.inlinePillButton}
            >
              {I18n.get('avatarInstructions')}
            </PillButton>
          </div>
          {acceptedFiles[0] && (
            <Typography variant='body2'>
              {I18n.get('selectedImage')} {acceptedFiles[0].name}
            </Typography>
          )}
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              variant='outlined'
              label={I18n.get('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
              onFocus={() => setErrors({ ...errors, firstName: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='firstName'
              required
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant='outlined'
              label={I18n.get('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
              onFocus={() => setErrors({ ...errors, lastName: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='lastName'
              required
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant='outlined'
              label={I18n.get('companyName')}
              error={!!errors.company}
              helperText={errors.company}
              onFocus={() => setErrors({ ...errors, company: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='company'
              required
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant='outlined'
              label={I18n.get('jobTitle')}
              error={!!errors.jobTitle}
              helperText={errors.jobTitle}
              onFocus={() => setErrors({ ...errors, jobTitle: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='jobTitle'
              required
              onChange={handleChange}
            />
          </Grid>

          {/* <Grid item xs={12}>
            <Typography variant='h5' classes={{ root: classes.spaceAbove }}>
              {I18n.get('mailingAddress')}
            </Typography>
            <Typography variant='body1' paragraph>
              {I18n.get('mailingAddressReason')}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant='outlined'
              label={I18n.get('address')}
              error={!!errors.address1}
              helperText={errors.address1}
              onFocus={() => setErrors({ ...errors, address1: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='address1'
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant='outlined'
              label={I18n.get('addressLineTwo')}
              error={!!errors.address2}
              helperText={errors.address2}
              onFocus={() => setErrors({ ...errors, address2: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='address2'
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant='outlined'
              label={I18n.get('titlePosition')}
              error={!!errors.jobTitle}
              helperText={errors.jobTitle}
              onFocus={() => setErrors({ ...errors, jobTitle: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='city'
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant='outlined'
              label={I18n.get('state')}
              error={!!errors.state}
              helperText={errors.state}
              onFocus={() => setErrors({ ...errors, state: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='state'
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant='outlined'
              label={I18n.get('zip')}
              error={!!errors.postalCode}
              helperText={errors.postalCode}
              onFocus={() => setErrors({ ...errors, postalCode: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='postalCode'
              onChange={handleChange}
            />
          </Grid>
            */}
          <Grid item xs={12}>
            <TextField
              required
              variant='outlined'
              label={I18n.get('country')}
              error={!!errors.country}
              helperText={errors.country}
              onFocus={() => setErrors({ ...errors, country: '' })}
              fullWidth
              className={classes.input}
              type='text'
              name='country'
              onChange={handleChange}
            />
          </Grid>
        </Grid>
        <Grid item>
          <PillButton
            type='submit'
            onClick={() => submitUser()}
            loading={loading}
            className={classes.button}
            backgroundColor='transparent'
          >
            {I18n.get('continue')}
          </PillButton>
        </Grid>
      </Grid>
    </Grid>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    width: 165,
    marginTop: '1rem',
    [theme.breakpoints.down('md')]: {
      marginBottom: '1rem',
      backgroundColor: '#fff !important'
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  dragDrop: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 80,
    backgroundColor: '#eee',
    marginBottom: '1rem',
    cursor: 'pointer',
    padding: '0 .5rem',
    '& span': {
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  },
  heading: {
    fontWeight: 700,
    fontSize: '3.125rem',
    lineHeight: '3.125rem',
    marginBottom: '2rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '3rem',
      lineHeight: '3rem'
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '2rem'
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '2rem',
      lineHeight: '2rem'
    }
  },
  input: {
    color: '#000',
    borderRadius: 0,
    '& .MuiInputBase-root': {
      backgroundColor: '#fff'
    },
    '& label': {
      color: theme.palette.grey[500]
    },
    '& fieldset': {
      borderRadius: 0,
      borderColor: '#dadada',
      borderBottomColor: '#000'
    }
  },
  inlineButton: {
    color: '#000',
    margin: '0 .5rem',
    fontWeight: 'normal',
    textDecoration: 'underline',
    verticalAlign: 'initial'
  },
  spaceAbove: {
    marginTop: '2rem'
  },
  inlineAvatarIcon: {
    position: 'relative',
    top: '12px',
    display: 'inline-block',
    marginTop: '20px',
    marginRight: '20px'
  },
  inlineAvatarUpload: {
    display: 'inline-block',
    marginTop: '1rem',
    marginBottom: '1rem'
  },
  inlinePillButton: {
    minWidth: 165,
    [theme.breakpoints.down('md')]: {
      marginBottom: '1rem',
      backgroundColor: '#fff !important'
    }
  }
}))
